import { Route, Routes, useParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useCanUpdateScan from "shared/Scan/access/useCanUpdateScan";

import { SCANS_FILTERS_ORDER_KEY } from "../components/Table/constants";
import { ScanSettingsContextData } from "./Context";
import ScanSettingsCloudProvider from "./CloudProvider";
import ScanSettingsDetails from "./Details";
import ScanSettingsResourceScope from "./ResourceScope";
import useGetScanSettings from "./useGetScanSettings";
import ScanSettingsWorkerPool from "./WorkerPool";
import ScanSettingsRoutesLayout from "./components/RoutesLayout";

const ScanSettings = () => {
  const { scanId } = useParams<{ scanId: string }>();

  const { scanSettings, loading, error } = useGetScanSettings(scanId);

  useTitle(scanSettings?.name ? `Settings · ${scanSettings.name}` : "Settings");

  const canUpdateScanFn = useCanUpdateScan();

  useBreadcrumbs(
    [
      {
        title: "Resources",
        link: "/resources/iac-management",
      },
      {
        title: "Cloud scanning",
        link: getBreadcrumbsBackUrl("/resources/scans", SCANS_FILTERS_ORDER_KEY),
      },
      {
        title: scanSettings?.name || "",
        link: scanId ? `/resources/scans/${scanId}` : undefined,
      },
      {
        title: "Scan settings",
      },
    ],
    [scanSettings?.name, scanId]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !scanSettings) {
    return <PageLoading />;
  }

  if (!scanSettings) {
    return <NotFoundPage />;
  }

  const canUpdateScan = canUpdateScanFn(scanSettings);

  return (
    <ScanSettingsContextData.Provider value={{ scanSettings, canUpdateScan }}>
      <Routes>
        <Route element={<ScanSettingsRoutesLayout />}>
          <Route index element={<ScanSettingsDetails />} />
          <Route path="cloud-provider" element={<ScanSettingsCloudProvider />} />
          <Route path="worker-pool" element={<ScanSettingsWorkerPool />} />
          <Route path="resource-scope" element={<ScanSettingsResourceScope />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ScanSettingsContextData.Provider>
  );
};

export default ScanSettings;
